import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { toast } from "react-toastify";
import { DEFAULT_FROM_EMAIL, FROM_EMAILS } from "const/emailSenders";
import { invalidEmailsIn, isValidEmail, parseEmailList } from "utils/emailRecipients";
import { useInvoiceEmailRecipient, useSendInvoiceEmail } from "utils/invoiceEmailService";

const BLANK = { to: "", cc: "", bcc: "", subject: "", message: "" };

/**
 * Emails one invoice to the party it is billed to, as the PDF the dashboard prints.
 *
 * The recipient is fetched when the dialog opens rather than passed in, so an address filled in
 * elsewhere since the list was loaded is picked up. A bill-to with nothing on file opens with an
 * empty To box and an offer to keep whatever is typed on that record.
 */
const SendInvoiceEmailDialog = ({ open, onClose, invoiceId, invoiceNumber, onSent }) => {
  const [form, setForm] = useState(BLANK);
  const [fromEmail, setFromEmail] = useState(DEFAULT_FROM_EMAIL);
  const [saveRecipientEmail, setSaveRecipientEmail] = useState(true);
  const [touchedTo, setTouchedTo] = useState(false);

  const {
    data: recipient,
    isFetching,
    isError: recipientFailed,
  } = useInvoiceEmailRecipient(invoiceId, { enabled: open });

  // Seeded from the server each time the dialog opens, never merged into what is already typed:
  // a half-written address must not be overwritten by a background refetch.
  useEffect(() => {
    if (!open) return;
    setForm(BLANK);
    setFromEmail(DEFAULT_FROM_EMAIL);
    setSaveRecipientEmail(true);
    setTouchedTo(false);
  }, [open, invoiceId]);

  useEffect(() => {
    if (!open || !recipient) return;
    setForm((prev) => ({
      ...prev,
      to: prev.to || recipient.billToEmail || "",
      subject: prev.subject || recipient.defaultSubject || "",
    }));
  }, [open, recipient]);

  const setField = (name) => (event) =>
    setForm((prev) => ({ ...prev, [name]: event.target.value }));

  const { mutate: sendInvoice, isLoading: sending } = useSendInvoiceEmail({
    onSuccess: (data) => {
      toast.success(
        data?.recipientEmailSaved
          ? `Invoice sent to ${data.to} and saved to their record`
          : `Invoice sent to ${data?.to || form.to}`
      );
      onSent?.(data);
      onClose?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage || "Failed to send the invoice");
    },
  });

  const badCc = invalidEmailsIn(form.cc);
  const badBcc = invalidEmailsIn(form.bcc);
  const toIsValid = isValidEmail(form.to);
  const canSend = toIsValid && badCc.length === 0 && badBcc.length === 0 && !sending && !isFetching;
  // Only offered when the bill-to has a record to keep it on, and only when it would change something.
  const offerToSave =
    Boolean(recipient?.canSaveEmail) &&
    toIsValid &&
    form.to.trim() !== (recipient?.billToEmail || "");

  const handleSend = () => {
    if (!canSend) return;
    sendInvoice({
      invoiceId,
      to: form.to.trim(),
      cc: parseEmailList(form.cc),
      bcc: parseEmailList(form.bcc),
      fromEmail,
      subject: form.subject.trim(),
      message: form.message.trim(),
      saveRecipientEmail: offerToSave && saveRecipientEmail,
    });
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="font-semibold">
        Email invoice {invoiceNumber || recipient?.attachmentFileName?.replace(/\.pdf$/, "") || ""}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isFetching && !recipient ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading recipient…
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sends the invoice to {recipient?.billToName || "the billed party"} as a PDF
              attachment.
            </Typography>
          )}

          {recipientFailed && (
            <Alert severity="warning">
              Could not load who this invoice is billed to. Type the address to send it to below.
            </Alert>
          )}

          {recipient && !recipient.billToEmail && (
            <Alert severity="info">
              {recipient.billToName || "This party"} has no email on file. Enter one below
              {recipient.canSaveEmail ? " and it can be kept on their record." : "."}
            </Alert>
          )}

          <TextField
            label="To"
            size="small"
            fullWidth
            required
            value={form.to}
            onChange={setField("to")}
            onBlur={() => setTouchedTo(true)}
            error={touchedTo && !toIsValid}
            helperText={touchedTo && !toIsValid ? "Enter a valid email address" : " "}
            disabled={sending}
          />

          {offerToSave && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={saveRecipientEmail}
                  onChange={(event) => setSaveRecipientEmail(event.target.checked)}
                  disabled={sending}
                />
              }
              label={`Also save this address to ${recipient?.billToName || "their"} record`}
              slotProps={{ typography: { fontSize: 14 } }}
            />
          )}

          <TextField
            label="CC"
            size="small"
            fullWidth
            value={form.cc}
            onChange={setField("cc")}
            error={badCc.length > 0}
            helperText={
              badCc.length > 0
                ? `Not a valid address: ${badCc.join(", ")}`
                : "Optional. Separate with commas."
            }
            disabled={sending}
          />
          <TextField
            label="BCC"
            size="small"
            fullWidth
            value={form.bcc}
            onChange={setField("bcc")}
            error={badBcc.length > 0}
            helperText={
              badBcc.length > 0
                ? `Not a valid address: ${badBcc.join(", ")}`
                : "Optional. Separate with commas."
            }
            disabled={sending}
          />

          <Autocomplete
            options={FROM_EMAILS}
            value={fromEmail}
            onChange={(_, value) => setFromEmail(value || DEFAULT_FROM_EMAIL)}
            disableClearable
            disabled={sending}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField {...params} label="Send from" size="small" fullWidth />
            )}
          />

          <TextField
            label="Subject"
            size="small"
            fullWidth
            value={form.subject}
            onChange={setField("subject")}
            disabled={sending}
          />
          <TextField
            label="Message"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={form.message}
            onChange={setField("message")}
            helperText="Optional note above the invoice summary."
            disabled={sending}
          />

          {recipient?.attachmentFileName && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <AttachFileOutlinedIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {recipient.attachmentFileName}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSend} disabled={!canSend}>
          {sending ? "Sending…" : "Send invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendInvoiceEmailDialog;
